import React, { useCallback, useContext, useMemo } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'

import { Div, P } from '@driftt/tide-core'

import { DateRange } from 'components/DateRangePicker'
import RefreshActivity from 'components/RefreshActivity'
import { driftLoadingStateBuilder } from 'components/States'
import { SortContext } from 'context/sortContext'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { useEmptyComponent } from 'hooks/useEmptyComponent'
import { useInsightsFeed } from 'hooks/useInsightsFeed'
import { SessionMetrics } from 'hooks/useSessionMetrics'
import CCRMFeedCard from 'sections/CCRM/FeedCard'
import { FilterPage } from 'utils/filters'
import { segmentTrack, TrackType } from 'utils/tracking'

import styles from './styles.module.css'

const PAGE_SIZE = 10
const SCROLL_TRACKING_DEBOUNCE_MS = 300

interface CCRMFeedProps {
  accountId?: string
  contactId?: string
  scrollTargetId?: string
  onScroll?: (scrollOffset: number) => void
  sessionMetrics: SessionMetrics
  withRefresh?: boolean
  withFilters?: boolean
  includeAllTimeActivities?: boolean
  includeSortControl?: boolean
  emptyComponent?: () => JSX.Element
  dateRange?: DateRange
  initialScrollY?: number
  hideAccountSummary?: boolean
}

const CCRMFeed: React.FC<CCRMFeedProps> = ({
  accountId,
  contactId,
  scrollTargetId,
  onScroll,
  sessionMetrics,
  withRefresh = false,
  withFilters = true,
  includeAllTimeActivities = false,
  includeSortControl = true,
  emptyComponent,
  dateRange,
  initialScrollY,
  hideAccountSummary
}) => {
  const { activitiesSort, activitiesDescending } = useContext(SortContext)

  const insightsFeed = useInsightsFeed(
    FilterPage.ACTIVITIES,
    activitiesSort,
    activitiesDescending,
    PAGE_SIZE,
    !includeAllTimeActivities || !!dateRange,
    withFilters,
    accountId,
    contactId,
    dateRange,
    hideAccountSummary
  )
  const {
    data,
    size,
    setSize,
    isLoadingInitialData,
    hasLoadedInitialData,
    isLoadingMore,
    isReachingEnd
  } = insightsFeed.insights
  const EmptyComponent = useEmptyComponent()
  const CustomEmptyComponent = emptyComponent
  const LoadingComponent = useMemo(() => driftLoadingStateBuilder('Loading your feed'), [])

  const loadMore = useCallback(() => {
    return !isReachingEnd && !isLoadingMore && !isLoadingInitialData && setSize((size ?? 0) + 1)
  }, [isReachingEnd, isLoadingMore, isLoadingInitialData, setSize, size])

  const sendTrackingEvent = useCallback(offset => {
    segmentTrack(TrackType.INSIGHT_FEED_SCROLL, {
      scrollOffset: offset,
      ui_version: 'CCRM',
      location: { ...window.document.location }
    })
  }, [])

  const debouncedScrollTrack = useDebouncedCallback((offset: number) => {
    onScroll && onScroll(offset)
    sendTrackingEvent(offset)
  }, SCROLL_TRACKING_DEBOUNCE_MS)

  const handleRefresh = () => {
    insightsFeed.refresh()
    // Set scrollY to the top when refreshing the insightFeed
    onScroll && onScroll(0)
  }
  const FeedCards = useMemo(
    () =>
      data.map(insight => (
        <CCRMFeedCard
          key={insight.id}
          insight={insight}
          countCardDwellTime={sessionMetrics.countCardDwellTime}
        />
      )),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  const FeedContainer = useMemo(
    () => (
      <InfiniteScroll
        dataLength={data.length}
        next={loadMore}
        hasMore={!isReachingEnd}
        scrollableTarget={scrollTargetId}
        initialScrollY={initialScrollY}
        onScroll={({ target }: any) => {
          debouncedScrollTrack(target.scrollTop)
        }}
        loader={<LoadingComponent />}
        endMessage={<></>} // nothing for now, could be handy in the future
      >
        {FeedCards}
      </InfiniteScroll>
    ),
    [
      data,
      loadMore,
      isReachingEnd,
      scrollTargetId,
      initialScrollY,
      debouncedScrollTrack,
      LoadingComponent,
      FeedCards
    ]
  )

  if (isLoadingInitialData) {
    return (
      <Div className={styles.emptyView}>
        <LoadingComponent />
      </Div>
    )
  }

  if (!isLoadingInitialData && data.length === 0) {
    return (
      <Div className={styles.emptyView}>
        {CustomEmptyComponent ? <CustomEmptyComponent /> : <EmptyComponent />}
      </Div>
    )
  }

  return (
    <>
      {hasLoadedInitialData && withRefresh && data.length > 0 && (
        <div className={styles.refreshButtonContainer}>
          <RefreshActivity
            mostRecentActivityAt={data[0].lastActivityAt}
            onClick={handleRefresh}
            filterPage={FilterPage.ACTIVITIES}
            accountId={accountId}
            fixedWhenScrolled
          />
        </div>
      )}
      {!isLoadingInitialData && data.length > 0 && (
        <>
          {includeSortControl && (
            <Div className={styles.sortIndicator}>
              <Div className={styles.sortLineBreak} />
              <P>
                Sorted by <span className={styles.sortValue}>Most recent activity</span>
              </P>
            </Div>
          )}
          {FeedContainer}
        </>
      )}
    </>
  )
}

export default CCRMFeed
